// File: src/ImageLightbox.jsx
import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, X, Trash2, Download, Loader2 } from 'lucide-react';
import { auth } from './firebase';

const ImageLightbox = ({ images, initialIndex, onClose, canDelete, onDelete }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [imageLoading, setImageLoading] = useState(true);
  
  // Refs per gestire il touch senza ri-renderizzare il componente (fluidità massima)
  const touchStart = useRef(null);
  const touchEnd = useRef(null);

  const currentImage = images[currentIndex];

  // 1. Reset loading quando cambia immagine
  useEffect(() => {
    setImageLoading(true);
  }, [currentIndex]);

  // 2. PRELOAD: Carica silenziosamente la prossima e la precedente immagine
  useEffect(() => {
    if (!images || images.length <= 1) return;

    const preloadImage = (index) => {
      const img = new Image();
      img.src = images[index].url;
    };

    const nextIndex = (currentIndex + 1) % images.length;
    const prevIndex = (currentIndex - 1 + images.length) % images.length;

    preloadImage(nextIndex);
    preloadImage(prevIndex);
  }, [currentIndex, images]);

  // Navigazione tastiera
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') handlePrevious();
      if (e.key === 'ArrowRight') handleNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, onClose]);

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
  };

  // --- GESTIONE SWIPE OTTIMIZZATA ---
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    touchEnd.current = null;
    touchStart.current = e.targetTouches[0].clientX;
  };

  const onTouchMove = (e) => {
    // NON facciamo nulla qui che causi re-render. Salviamo solo la coordinata.
    touchEnd.current = e.targetTouches[0].clientX;
  };

  const onTouchEnd = () => {
    if (!touchStart.current || !touchEnd.current) return;
    const distance = touchStart.current - touchEnd.current;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) handleNext();
    if (isRightSwipe) handlePrevious();
  };

  const handleDelete = () => {
    if (onDelete && canDelete(currentImage)) {
      onDelete(currentImage);
      if (images.length === 1) {
        onClose();
      } else if (currentIndex >= images.length - 1) {
        setCurrentIndex(Math.max(0, currentIndex - 1));
      }
    }
  };

  const handleDownload = async () => {
    try {
      const token = await auth.currentUser.getIdToken();
      const fileName = currentImage.fileName || `photo-${currentIndex + 1}.jpg`;
      const encodedUrl = encodeURIComponent(currentImage.url);
      const encodedFilename = encodeURIComponent(fileName);
      
      const functionUrl = `https://europe-west1-itad-app.cloudfunctions.net/downloadImage?url=${encodedUrl}&filename=${encodedFilename}`;
      
      const response = await fetch(functionUrl, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Download failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 100);
      
    } catch (error) {
      console.error('Download error:', error);
      alert('Error downloading image. Please try again.');
    }
  };

  return (
    <div 
      className="modal-overlay" 
      style={{ 
        background: 'rgba(0,0,0,0.95)', 
        zIndex: 2000,
        padding: 0,
        overflow: 'hidden' // Impedisce lo scroll della pagina di fondo
      }}
      onClick={onClose}
    >
      {/* HEADER */}
      <div 
        style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, padding: '1rem',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          zIndex: 2001,
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.6), transparent)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ color: 'white', fontSize: '0.9rem', fontWeight: 500 }}>
          {currentIndex + 1} / {images.length}
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          {canDelete && canDelete(currentImage) && (
            <button
              onClick={handleDelete}
              style={{
                background: 'rgba(255, 68, 68, 0.9)', color: 'white', border: 'none',
                borderRadius: '8px', padding: '8px', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}
              title="Delete"
            >
              <Trash2 size={20} />
            </button>
          )}
          <button
            onClick={handleDownload}
            style={{
              background: 'rgba(255, 255, 255, 0.2)', color: 'white', border: 'none',
              borderRadius: '8px', padding: '8px', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}
            title="Download"
          >
            <Download size={20} />
          </button>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.2)', color: 'white', border: 'none',
              borderRadius: '8px', padding: '8px', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}
          >
            <X size={24} />
          </button>
        </div>
      </div>

      {/* AREA CONTENUTO CENTRALE */}
      <div 
        style={{
          height: '100vh', 
          width: '100vw', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          padding: 0,
          touchAction: 'pan-y' // Permette lo scroll verticale ma cattura quello orizzontale in JS
        }}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {imageLoading && (
          <div style={{ position: 'absolute', zIndex: 10 }}>
            <Loader2 className="animate-spin" size={48} color="white" />
          </div>
        )}

        {currentImage.mimeType && currentImage.mimeType.startsWith('video/') ? (
          <video 
            src={currentImage.url} 
            controls 
            autoPlay 
            playsInline // <--- FONDAMENTALE PER MOBILE (evita fullscreen forzato su iPhone)
            onLoadedData={() => setImageLoading(false)}
            style={{ maxWidth: '100%', maxHeight: '100%', boxShadow: '0 4px 20px rgba(0,0,0,0.5)' }}
          />
        ) : (
          <img 
            src={currentImage.url} 
            alt={currentImage.description || 'Photo'}
            onLoad={() => setImageLoading(false)}
            decoding="async" // <--- OTTIMIZZAZIONE: Decodifica fuori dal thread principale
            style={{ 
              maxWidth: '100%', 
              maxHeight: '100%', 
              objectFit: 'contain', 
              userSelect: 'none',
              opacity: imageLoading ? 0 : 1, 
              transition: 'opacity 0.2s ease',
              willChange: 'transform' // <--- OTTIMIZZAZIONE GPU
            }}
          />
        )}
      </div>

      {/* FRECCE DESKTOP (Nascoste su mobile) */}
      {images.length > 1 && (
        <div className="desktop-arrows" style={{display: 'none', '@media (min-width: 768px)': {display: 'block'}}}>
          <button
            onClick={(e) => { e.stopPropagation(); handlePrevious(); }}
            style={{
              position: 'fixed', left: '20px', top: '50%', transform: 'translateY(-50%)',
              background: 'rgba(255,255,255,0.15)', color: 'white', border: 'none',
              borderRadius: '50%', width: '50px', height: '50px',
              display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 2001,
              backdropFilter: 'blur(4px)'
            }}
          >
            <ChevronLeft size={30} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); handleNext(); }}
            style={{
              position: 'fixed', right: '20px', top: '50%', transform: 'translateY(-50%)',
              background: 'rgba(255,255,255,0.15)', color: 'white', border: 'none',
              borderRadius: '50%', width: '50px', height: '50px',
              display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 2001,
              backdropFilter: 'blur(4px)'
            }}
          >
            <ChevronRight size={30} />
          </button>
        </div>
      )}

      {/* DESCRIZIONE - Footer Ottimizzato */}
      {currentImage.description && (
        <div 
          style={{
            position: 'fixed', 
            bottom: '20px', 
            left: '50%', 
            transform: 'translateX(-50%)', 
            width: '90%', 
            maxWidth: '600px', 
            padding: '12px 16px',
            // RIMOSSO backdropFilter pesante su mobile, usato un nero più solido
            background: 'rgba(20, 20, 20, 0.85)', 
            border: '1px solid rgba(255,255,255,0.1)',
            color: '#eee', 
            textAlign: 'center', 
            borderRadius: '12px',
            zIndex: 2001,
            boxShadow: '0 4px 10px rgba(0,0,0,0.5)'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <p style={{ margin: 0, fontSize: '0.95rem', lineHeight: '1.4' }}>
            {currentImage.description}
          </p>
        </div>
      )}
    </div>
  );
};

export default ImageLightbox;